import { call } from '@advanza/api'
import { HybridTooltip, Icon, LoadingDots } from '@advanza/ui'
import {
    DebtorBucket,
    DebtorOverviewMonth,
    DebtorOverviewResponse,
} from '@trustoo/types/api/controllers/Invoices'
import clsx from 'clsx'
import { formatEuro, formatPercent1, percentageOf } from 'components/billing/debtorNew/format'
import {
    Card,
    Funnel,
    OPEN_STATUS_KEYS,
    Segmented,
    StatusBar,
    StatusLegend,
} from 'components/billing/debtorNew/ui'
import { format } from 'date-fns'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

type MonthBasis = 'about' | 'sent'
type InvoiceType = 'all' | 'regular' | 'interim'
type MonthsBack = 6 | 12 | 24

function useDebtorOverview(
    country: string,
    monthBasis: MonthBasis,
    invoiceType: InvoiceType,
    months: MonthsBack,
) {
    const [data, setData] = useState<DebtorOverviewMonth[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)

    useEffect(() => {
        if (!country) {
            return
        }
        // Ignore-flag so a slow response for old params can't clobber a newer one.
        let ignore = false
        setIsLoading(true)
        setHasError(false)
        call('office/invoices/get-debtor-overview-v2', {
            query: { country, monthBasis, invoiceType, months },
        }).then(
            (response: DebtorOverviewResponse) => {
                if (ignore) {
                    return
                }
                setData(response.months || [])
                setIsLoading(false)
            },
            () => {
                if (ignore) {
                    return
                }
                setHasError(true)
                setIsLoading(false)
            },
        )
        return () => {
            ignore = true
        }
    }, [country, monthBasis, invoiceType, months])

    return { data, isLoading, hasError }
}

const openRateFill = (rate: number) => {
    if (rate >= 40) {
        return 'bg-red-400'
    }
    if (rate >= 20) {
        return 'bg-amber-400'
    }
    return 'bg-green-500'
}

const formatMonth = (month: string) => {
    const [year, m] = month.split('-')
    const date = new Date(Number(year), Number(m) - 1, 1)
    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

const OpenRateCell = ({ totals }: { totals: DebtorBucket['totals'] }) => {
    const rate = percentageOf(totals.openAmount, totals.inclVat, 1)
    return (
        <div className="flex items-center justify-end gap-2">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-neutral-100">
                <div
                    className={clsx('h-full rounded-full', openRateFill(rate))}
                    style={{ width: `${Math.min(rate, 100)}%` }}
                />
            </div>
            <span className="w-12 text-right tabular-nums text-neutral-700">
                {formatPercent1(rate)}%
            </span>
        </div>
    )
}

const AmountCell = ({ amount, muted }: { amount: number; muted?: boolean }) => (
    <div className={clsx('text-right tabular-nums', muted ? 'text-neutral-500' : 'text-dark')}>
        {formatEuro(amount)}
    </div>
)

const CountCell = ({ count }: { count: number }) => (
    <div className="text-right tabular-nums text-neutral-500">{count.toLocaleString('nl-NL')}</div>
)

const DetailsPanel = ({
    bucket,
    invoicesMonth,
}: {
    bucket: DebtorBucket
    invoicesMonth: string | null
}) => (
    <div className="grid gap-6 lg:grid-cols-2">
        <div>
            <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-500">
                Status breakdown
            </h5>
            <StatusBar bucket={bucket} />
            {bucket.totals.openAmount > 0 && (
                <div className="mt-3">
                    <div className="mb-1 text-sm text-neutral-400">
                        Open only · {formatEuro(bucket.totals.openAmount)}
                    </div>
                    <StatusBar bucket={bucket} keys={OPEN_STATUS_KEYS} />
                </div>
            )}
            <div className="mt-3">
                <StatusLegend bucket={bucket} invoicesMonth={invoicesMonth} />
            </div>
        </div>
        <div className="lg:border-l lg:border-neutral-100 lg:pl-6">
            <h5 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-500">
                Payment funnel
            </h5>
            <Funnel bucket={bucket} />
        </div>
    </div>
)

/* -------------------------------------------------------------------------- */
/*  Per-service drill-down table (client-side sortable)                       */
/* -------------------------------------------------------------------------- */

type ServiceSortField = 'name' | 'exclVat' | 'inclVat' | 'count' | 'open' | 'openCount' | 'rate'

const serviceSortValue = (bucket: DebtorBucket, field: ServiceSortField): number | string => {
    switch (field) {
        case 'name':
            return bucket.serviceName ?? ''
        case 'exclVat':
            return bucket.totals.exclVat
        case 'inclVat':
            return bucket.totals.inclVat
        case 'count':
            return bucket.totals.count
        case 'open':
            return bucket.totals.openAmount
        case 'openCount':
            return bucket.totals.openCount
        case 'rate':
            return percentageOf(bucket.totals.openAmount, bucket.totals.inclVat, 1)
    }
}

const SortableTh = ({
    label,
    field,
    sortField,
    sortDir,
    onSort,
    alignLeft,
}: {
    label: string
    field: ServiceSortField
    sortField: ServiceSortField
    sortDir: 'asc' | 'desc'
    onSort: (field: ServiceSortField) => void
    alignLeft?: boolean
}) => (
    <th className={clsx('py-2 font-semibold', alignLeft ? 'text-left' : 'text-right')}>
        <button
            type="button"
            onClick={() => onSort(field)}
            className={clsx(
                'inline-flex items-center gap-0.5 uppercase tracking-wide hover:text-neutral-600',
                sortField === field ? 'text-neutral-600' : 'text-neutral-400',
            )}>
            {label}
            <Icon
                name={(() => {
                    if (sortField !== field) {
                        return 'unfold_more'
                    }
                    return sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'
                })()}
                fontSize={13}
            />
        </button>
    </th>
)

const ServicesTable = ({
    services,
    invoicesMonth,
}: {
    services: DebtorBucket[]
    /** About-month (YYYY-MM) used to deep-link rows to the invoices tab; null disables links. */
    invoicesMonth: string | null
}) => {
    const [sortField, setSortField] = useState<ServiceSortField>('inclVat')
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

    const onSort = (field: ServiceSortField) => {
        if (field === sortField) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDir(field === 'name' ? 'asc' : 'desc')
        }
    }

    const sorted = useMemo(() => {
        const factor = sortDir === 'asc' ? 1 : -1
        return [...services].sort((a, b) => {
            const va = serviceSortValue(a, sortField)
            const vb = serviceSortValue(b, sortField)
            if (typeof va === 'string' || typeof vb === 'string') {
                return String(va).localeCompare(String(vb)) * factor
            }
            return (va - vb) * factor
        })
    }, [services, sortField, sortDir])

    const thProps = { sortField, sortDir, onSort }

    return (
        <table className="mt-3 w-full text-md">
            <thead>
                <tr className="whitespace-nowrap text-sm uppercase tracking-wide text-neutral-400">
                    <SortableTh label="Service" field="name" alignLeft {...thProps} />
                    <SortableTh label="Excl. VAT" field="exclVat" {...thProps} />
                    <SortableTh label="Incl. VAT" field="inclVat" {...thProps} />
                    <SortableTh label="# Inv" field="count" {...thProps} />
                    <SortableTh label="Open" field="open" {...thProps} />
                    <SortableTh label="# Open" field="openCount" {...thProps} />
                    <SortableTh label="% Open" field="rate" {...thProps} />
                </tr>
            </thead>
            <tbody>
                {sorted.map((service) => (
                    <tr key={service.serviceId ?? 'none'} className="border-t border-neutral-100">
                        <td className="py-2 text-neutral-700">
                            <span className="inline-flex items-center gap-2">
                                {service.serviceName ?? 'No service'}
                                {invoicesMonth && service.serviceId !== null && (
                                    <Link
                                        to={`/billing/invoices?aboutMonth=${invoicesMonth}&serviceIds=${service.serviceId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Open this month's invoices for this service in the invoices tab (matches providers with an active subscription on the service, so counts can differ slightly)"
                                        className="text-neutral-300 hover:text-primary-600">
                                        <Icon name="open_in_new" fontSize={14} />
                                    </Link>
                                )}
                            </span>
                        </td>
                        <td className="py-2 pl-3">
                            <AmountCell amount={service.totals.exclVat} muted />
                        </td>
                        <td className="py-2 pl-3">
                            <AmountCell amount={service.totals.inclVat} />
                        </td>
                        <td className="py-2 pl-3">
                            <CountCell count={service.totals.count} />
                        </td>
                        <td className="py-2 pl-3">
                            <AmountCell amount={service.totals.openAmount} />
                        </td>
                        <td className="py-2 pl-3">
                            <CountCell count={service.totals.openCount} />
                        </td>
                        <td className="py-2 pl-3">
                            <OpenRateCell totals={service.totals} />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

const COL_COUNT = 8

interface Props {
    country: string
}

const OverviewTab = ({ country }: Props) => {
    // Controls live in the URL so views are shareable and survive a refresh.
    const [searchParams, setSearchParams] = useSearchParams()
    const monthBasis: MonthBasis = searchParams.get('basis') === 'about' ? 'about' : 'sent'
    const typeParam = searchParams.get('type')
    const invoiceType: InvoiceType = (() => {
        if (typeParam === 'regular' || typeParam === 'interim') {
            return typeParam
        }
        return 'all'
    })()
    const monthsParam = searchParams.get('months')
    const monthsBack: MonthsBack = (() => {
        if (monthsParam === '6') {
            return 6
        }
        if (monthsParam === '24') {
            return 24
        }
        return 12
    })()
    const setParam = (key: string, value: string) => {
        setSearchParams(
            (prev) => {
                prev.set(key, value)
                return prev
            },
            { replace: true },
        )
    }
    const [openMonth, setOpenMonth] = useState<string | null>(null)
    const [servicesOpen, setServicesOpen] = useState<Record<string, boolean>>({})
    const { data, isLoading, hasError } = useDebtorOverview(
        country,
        monthBasis,
        invoiceType,
        monthsBack,
    )

    const grandTotal = useMemo(
        () =>
            data.reduce(
                (acc, month) => {
                    acc.inclVat += month.all.totals.inclVat
                    acc.exclVat += month.all.totals.exclVat
                    acc.count += month.all.totals.count
                    acc.openAmount += month.all.totals.openAmount
                    acc.openCount += month.all.totals.openCount
                    return acc
                },
                { inclVat: 0, exclVat: 0, count: 0, openAmount: 0, openCount: 0 },
            ),
        [data],
    )

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-md">
                    <span className="text-neutral-500">Group by</span>
                    <Segmented<MonthBasis>
                        value={monthBasis}
                        onChange={(value) => setParam('basis', value)}
                        options={[
                            { value: 'sent', label: 'Sent month' },
                            { value: 'about', label: 'About month' },
                        ]}
                    />
                </label>
                <label className="flex items-center gap-2 text-md">
                    <span className="text-neutral-500">Type</span>
                    <Segmented<InvoiceType>
                        value={invoiceType}
                        onChange={(value) => setParam('type', value)}
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'regular', label: 'Regular' },
                            { value: 'interim', label: 'Interim' },
                        ]}
                    />
                </label>
                <label className="flex items-center gap-2 text-md">
                    <span className="text-neutral-500">Months</span>
                    <Segmented<'6' | '12' | '24'>
                        value={String(monthsBack) as '6' | '12' | '24'}
                        onChange={(value) => setParam('months', value)}
                        options={[
                            { value: '6', label: 'L6M' },
                            { value: '12', label: 'L12M' },
                            { value: '24', label: 'L24M' },
                        ]}
                    />
                </label>
                {isLoading && (
                    <span className="text-md text-neutral-400">
                        Loading <LoadingDots />
                    </span>
                )}
            </div>

            {hasError ? (
                <p className="text-red-700">Failed loading the debtor overview.</p>
            ) : (
                <Card className="overflow-hidden">
                    <table className="w-full border-collapse text-md">
                        <thead>
                            <tr className="whitespace-nowrap border-b border-neutral-200 bg-neutral-50 text-sm uppercase tracking-wide text-neutral-500">
                                <th className="px-3 py-2.5 text-left font-semibold">Month</th>
                                <th className="px-3 py-2.5 text-right font-semibold">
                                    Invoiced excl. VAT
                                </th>
                                <th className="px-3 py-2.5 text-right font-semibold">
                                    Invoiced incl. VAT
                                </th>
                                <th className="px-3 py-2.5 text-right font-semibold"># Inv</th>
                                <th className="px-3 py-2.5 text-right font-semibold">Open</th>
                                <th className="px-3 py-2.5 text-right font-semibold"># Open</th>
                                <th className="px-3 py-2.5 text-right font-semibold">% Open</th>
                                <th className="w-10 px-3 py-2.5" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((monthData) => {
                                const monthKey = monthData.month
                                const isOpen = openMonth === monthKey
                                const isCurrentMonth = monthKey === format(new Date(), 'yyyy-MM')
                                const showServices = !!servicesOpen[monthKey]
                                return (
                                    <Fragment key={monthKey}>
                                        <tr
                                            className={clsx(
                                                'cursor-pointer border-b border-neutral-100 transition',
                                                isOpen ? 'bg-primary-50' : 'hover:bg-neutral-50',
                                            )}
                                            onClick={() => setOpenMonth(isOpen ? null : monthKey)}>
                                            <td className="whitespace-nowrap px-3 py-2.5 font-medium text-dark">
                                                {formatMonth(monthKey)}
                                                {isCurrentMonth && (
                                                    <HybridTooltip
                                                        childrenContent={
                                                            <div className="max-w-xs text-sm leading-relaxed">
                                                                Current month, still in progress:
                                                                invoicing and collection are not
                                                                finished, so the open figures will
                                                                look high.
                                                            </div>
                                                        }>
                                                        <span className="ml-2 cursor-help rounded-full bg-primary-50 px-2 py-0.5 text-sm font-medium text-primary-700">
                                                            in progress
                                                        </span>
                                                    </HybridTooltip>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <AmountCell
                                                    amount={monthData.all.totals.exclVat}
                                                    muted
                                                />
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <AmountCell amount={monthData.all.totals.inclVat} />
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <CountCell count={monthData.all.totals.count} />
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <AmountCell
                                                    amount={monthData.all.totals.openAmount}
                                                />
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <CountCell count={monthData.all.totals.openCount} />
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <OpenRateCell totals={monthData.all.totals} />
                                            </td>
                                            <td className="px-3 py-2.5 text-right text-neutral-400">
                                                <Icon
                                                    name={isOpen ? 'expand_less' : 'expand_more'}
                                                    fontSize={20}
                                                />
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="border-b border-neutral-100 bg-neutral-50">
                                                <td colSpan={COL_COUNT} className="px-4 py-5">
                                                    <DetailsPanel
                                                        bucket={monthData.all}
                                                        invoicesMonth={
                                                            monthBasis === 'about' ? monthKey : null
                                                        }
                                                    />
                                                    {monthData.services.length > 0 && (
                                                        <div className="mt-5 border-t border-neutral-200 pt-4">
                                                            <button
                                                                type="button"
                                                                className="flex items-center gap-1 text-md font-medium text-primary-700 hover:text-primary-800"
                                                                onClick={() =>
                                                                    setServicesOpen((prev) => ({
                                                                        ...prev,
                                                                        [monthKey]: !prev[monthKey],
                                                                    }))
                                                                }>
                                                                <Icon
                                                                    name={
                                                                        showServices
                                                                            ? 'expand_less'
                                                                            : 'expand_more'
                                                                    }
                                                                    fontSize={18}
                                                                />
                                                                {showServices ? 'Hide' : 'Show'} per
                                                                service ({monthData.services.length}
                                                                )
                                                            </button>
                                                            {showServices && (
                                                                <ServicesTable
                                                                    services={monthData.services}
                                                                    invoicesMonth={
                                                                        monthBasis === 'about'
                                                                            ? monthKey
                                                                            : null
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                )
                            })}
                            {!isLoading && data.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={COL_COUNT}
                                        className="py-10 text-center text-neutral-400">
                                        No invoices found for this country.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {data.length > 0 && (
                            <tfoot>
                                <tr className="border-t-2 border-neutral-200 bg-neutral-50 font-semibold text-dark">
                                    <td className="whitespace-nowrap px-3 py-2.5">
                                        Total · {data.length} mo
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <AmountCell amount={grandTotal.exclVat} muted />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <AmountCell amount={grandTotal.inclVat} />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <CountCell count={grandTotal.count} />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <AmountCell amount={grandTotal.openAmount} />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <CountCell count={grandTotal.openCount} />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <OpenRateCell totals={grandTotal} />
                                    </td>
                                    <td className="px-3 py-2.5" />
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </Card>
            )}
        </div>
    )
}

export default OverviewTab
